import React, { useState, useEffect } from 'react';
import { integrationService, BulkInvoiceResetResult, BulkInvoiceSyncResult, VatCodesCheckResult } from '../services/integrationService';
import { useTranslation } from '../hooks/useTranslation';
import WFirmaAccountSync from './WFirmaAccountSync';

interface WFirmaIntegrationSettingsProps {
  companyId: string;
}

const WFirmaIntegrationSettings: React.FC<WFirmaIntegrationSettingsProps> = ({ companyId }) => {
  const { t } = useTranslation();
  const [config, setConfig] = useState({
    access_key: '',
    secret_key: '',
    app_key: '',
    company_id: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [hasConfig, setHasConfig] = useState(false);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [periodDateField, setPeriodDateField] = useState<'ISSUE' | 'SALE'>('ISSUE');
  const [periodForceResync, setPeriodForceResync] = useState(false);
  const [periodSyncing, setPeriodSyncing] = useState(false);
  const [periodResult, setPeriodResult] = useState<BulkInvoiceSyncResult | null>(null);
  const [periodError, setPeriodError] = useState('');
  const [resetStart, setResetStart] = useState('');
  const [resetEnd, setResetEnd] = useState('');
  const [resetDateField, setResetDateField] = useState<'ISSUE' | 'SALE'>('ISSUE');
  const [resetStatuses, setResetStatuses] = useState<string[]>(['SENT']);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetResult, setResetResult] = useState<BulkInvoiceResetResult | null>(null);
  const [resetError, setResetError] = useState('');
  const [vatCheckLoading, setVatCheckLoading] = useState(false);
  const [vatCheckResult, setVatCheckResult] = useState<VatCodesCheckResult | null>(null);
  const [vatCheckError, setVatCheckError] = useState('');

  useEffect(() => {
    loadConfig();
  }, [companyId]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await integrationService.getIntegrationConfig(companyId, 'wfirma');
      setConfig({
        access_key: data.access_key || '',
        secret_key: data.secret_key || '',
        app_key: data.app_key || '',
        company_id: data.company_id || ''
      });
      setHasConfig(true);
    } catch (err: any) {
      // Если конфигурация не найдена, это нормально - просто оставляем пустые поля
      if (err.response?.status !== 500) {
        setConfig({
          access_key: '',
          secret_key: '',
          app_key: '',
          company_id: ''
        });
        setHasConfig(false);
      } else {
        setError(t('integrations.wfirma.loadError') || 'Ошибка загрузки конфигурации');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      // Валидация
      if (!config.access_key || !config.secret_key || !config.app_key || !config.company_id) {
        setError(t('integrations.wfirma.fillAllFields') || 'Заполните все поля');
        return;
      }

      await integrationService.saveIntegrationConfig(companyId, 'wfirma', config);
      setSuccess(t('integrations.wfirma.saveSuccess') || 'Конфигурация сохранена успешно');
      setHasConfig(true);
      
      // Очищаем сообщение об успехе через 3 секунды
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || t('integrations.wfirma.saveError') || 'Ошибка сохранения конфигурации');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('integrations.wfirma.deleteConfirm') || 'Вы уверены, что хотите удалить конфигурацию wFirma?')) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await integrationService.deleteIntegrationConfig(companyId, 'wfirma');
      setConfig({
        access_key: '',
        secret_key: '',
        app_key: '',
        company_id: ''
      });
      setSuccess(t('integrations.wfirma.deleteSuccess') || 'Конфигурация удалена');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || t('integrations.wfirma.deleteError') || 'Ошибка удаления конфигурации');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    setError('');
    setSuccess('');
  };

  const handleSyncInvoicesPeriod = async () => {
    if (!periodStart || !periodEnd) {
      setPeriodError(t('integrations.wfirma.periodDatesRequired') || 'Укажите даты начала и конца периода');
      return;
    }
    setPeriodError('');
    setPeriodResult(null);
    setPeriodSyncing(true);
    try {
      const res = await integrationService.syncInvoicesPeriod(companyId, {
        startDate: periodStart,
        endDate: periodEnd,
        forceResync: periodForceResync,
        dateField: periodDateField
      });
      setPeriodResult(res);
    } catch (err: any) {
      setPeriodError(err.response?.data?.message || err.message || 'Error');
    } finally {
      setPeriodSyncing(false);
    }
  };

  const handleResetInvoicesToDraft = async () => {
    if (!resetStart || !resetEnd) {
      setResetError(t('integrations.wfirma.periodDatesRequired') || 'Укажите даты начала и конца периода');
      return;
    }
    if (resetStatuses.length === 0) {
      setResetError(t('integrations.wfirma.resetStatusesRequired') || 'Выберите хотя бы один статус');
      return;
    }
    if (!window.confirm(
      t('integrations.wfirma.resetConfirm') ||
      'Перевести все отправленные фактуры за период обратно в черновики и очистить wFirma ID?'
    )) {
      return;
    }
    setResetError('');
    setResetResult(null);
    setResetLoading(true);
    try {
      const res = await integrationService.resetInvoicesToDraftPeriod(companyId, {
        startDate: resetStart,
        endDate: resetEnd,
        dateField: resetDateField,
        statuses: resetStatuses
      });
      setResetResult(res);
    } catch (err: any) {
      setResetError(err.response?.data?.message || err.message || 'Error');
    } finally {
      setResetLoading(false);
    }
  };

  const toggleResetStatus = (status: string) => {
    setResetStatuses(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const handleRefreshAndCheckVatCodes = async () => {
    setVatCheckError('');
    setVatCheckResult(null);
    setVatCheckLoading(true);
    try {
      const res = await integrationService.refreshAndCheckVatCodes(companyId);
      setVatCheckResult(res);
    } catch (err: any) {
      setVatCheckError(err.response?.data?.message || err.message || 'Error');
    } finally {
      setVatCheckLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          {t('integrations.wfirma.title') || 'Интеграция wFirma'}
        </h3>
        <p className="text-sm text-gray-600">
          {t('integrations.wfirma.description') || 'Настройте подключение к wFirma для синхронизации контрагентов'}
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-600 text-sm">{success}</p>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('integrations.wfirma.companyId') || 'ID компании в wFirma'} *
          </label>
          <input
            type="text"
            value={config.company_id}
            onChange={(e) => handleChange('company_id', e.target.value)}
            placeholder="876505"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="mt-1 text-xs text-gray-500">
            {t('integrations.wfirma.companyIdHint') || 'ID вашей компании в системе wFirma'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('integrations.wfirma.accessKey') || 'Access Key'} *
          </label>
          <input
            type="password"
            value={config.access_key}
            onChange={(e) => handleChange('access_key', e.target.value)}
            placeholder={t('integrations.wfirma.accessKeyPlaceholder') || 'Введите Access Key'}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('integrations.wfirma.secretKey') || 'Secret Key'} *
          </label>
          <input
            type="password"
            value={config.secret_key}
            onChange={(e) => handleChange('secret_key', e.target.value)}
            placeholder={t('integrations.wfirma.secretKeyPlaceholder') || 'Введите Secret Key'}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="mt-1 text-xs text-gray-500">
            {t('integrations.wfirma.secretKeyHint') || 'Отображается только один раз при создании'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('integrations.wfirma.appKey') || 'App Key'} *
          </label>
          <input
            type="password"
            value={config.app_key}
            onChange={(e) => handleChange('app_key', e.target.value)}
            placeholder={t('integrations.wfirma.appKeyPlaceholder') || 'Введите App Key'}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="mt-1 text-xs text-gray-500">
            {t('integrations.wfirma.appKeyHint') || 'Выдается wFirma.pl индивидуально для каждого приложения'}
          </p>
        </div>

        <div className="flex gap-3 pt-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (t('common.saving') || 'Сохранение...') : (t('common.save') || 'Сохранить')}
          </button>
          
          {(config.access_key || config.secret_key || config.app_key || config.company_id) && (
            <button
              onClick={handleDelete}
              disabled={saving}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.delete') || 'Удалить'}
            </button>
          )}
        </div>
      </div>

      {/* Секция управления счетами wFirma */}
      {hasConfig && (
        <div className="mt-8 pt-8 border-t border-gray-200">
          <WFirmaAccountSync companyId={companyId} />
        </div>
      )}

      {hasConfig && (
        <div className="mt-8 pt-8 border-t border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-2">
            {t('integrations.wfirma.vatCodesTitle') || 'Проверка VAT-кодов wFirma'}
          </h4>
          <p className="text-sm text-gray-600 mb-4">
            {t('integrations.wfirma.vatCodesHint') ||
              'Обновляет VAT-коды из текущего аккаунта wFirma и показывает базовую проверку ключевых ставок/кодов.'}
          </p>
          <button
            type="button"
            onClick={handleRefreshAndCheckVatCodes}
            disabled={vatCheckLoading}
            className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50"
          >
            {vatCheckLoading
              ? t('common.loading') || '...'
              : t('integrations.wfirma.vatCodesRefreshButton') || 'Обновить и проверить VAT-коды'}
          </button>
          {vatCheckError && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{vatCheckError}</div>
          )}
          {vatCheckResult && (
            <div className="mt-3 p-4 bg-gray-50 border border-gray-200 rounded-lg text-sm space-y-1">
              <p><strong>{t('integrations.wfirma.vatCodesTotal') || 'Всего VAT-кодов'}:</strong> {vatCheckResult.totalCount}</p>
              <p>{vatCheckResult.hasRate23 ? '✅' : '❌'} 23%</p>
              <p>{vatCheckResult.hasRate8 ? '✅' : '❌'} 8%</p>
              <p>{vatCheckResult.hasRate0 ? '✅' : '❌'} 0%</p>
              <p>{vatCheckResult.hasZw ? '✅' : '❌'} ZW</p>
              <p>{vatCheckResult.hasNp ? '✅' : '❌'} NP</p>
              <p>{vatCheckResult.hasNpue ? '✅' : '❌'} NPUE</p>
              {vatCheckResult.vatCodes?.length > 0 && (
                <div className="mt-3">
                  <p className="font-semibold mb-1">
                    {t('integrations.wfirma.vatCodesListTitle') || 'Список VAT-кодов из wFirma'}
                  </p>
                  <div className="max-h-64 overflow-auto border border-gray-200 rounded bg-white">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="text-left px-2 py-1">wFirma ID</th>
                          <th className="text-left px-2 py-1">Code</th>
                          <th className="text-left px-2 py-1">Name</th>
                          <th className="text-left px-2 py-1">Rate</th>
                          <th className="text-left px-2 py-1">EU</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vatCheckResult.vatCodes.map((row, idx) => (
                          <tr key={`${row.wfirmaId}-${idx}`} className="border-t border-gray-100">
                            <td className="px-2 py-1">{row.wfirmaId}</td>
                            <td className="px-2 py-1">{row.code || '-'}</td>
                            <td className="px-2 py-1">{row.name || '-'}</td>
                            <td className="px-2 py-1">{row.rate ?? '-'}</td>
                            <td className="px-2 py-1">{row.isEu ? 'true' : 'false'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {hasConfig && (
        <div className="mt-8 pt-8 border-t border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-2">
            {t('integrations.wfirma.bulkInvoicesTitle') || 'Отправка фактур в wFirma за период'}
          </h4>
          <p className="text-sm text-gray-600 mb-4">
            {t('integrations.wfirma.bulkInvoicesHint') ||
              'Выберите период. Без «Повторная отправка» пропускаются уже синхронизированные фактуры. С включённой опцией старые привязки сбрасываются и фактуры создаются заново в wFirma.'}
          </p>
          <div className="flex flex-wrap gap-4 items-end mb-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('integrations.wfirma.periodFrom') || 'С'}</label>
              <input
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('integrations.wfirma.periodTo') || 'По'}</label>
              <input
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('integrations.wfirma.dateField') || 'Период по'}</label>
              <select
                value={periodDateField}
                onChange={(e) => setPeriodDateField(e.target.value as 'ISSUE' | 'SALE')}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="ISSUE">{t('integrations.wfirma.dateFieldIssue') || 'Дата выставления'}</option>
                <option value="SALE">{t('integrations.wfirma.dateFieldSale') || 'Дата продажи'}</option>
              </select>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={periodForceResync}
                onChange={(e) => setPeriodForceResync(e.target.checked)}
              />
              <span className="text-sm text-gray-700">
                {t('integrations.wfirma.forceResync') || 'Повторная отправка (сбросить старую привязку wFirma)'}
              </span>
            </label>
            <button
              type="button"
              onClick={handleSyncInvoicesPeriod}
              disabled={periodSyncing}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {periodSyncing
                ? t('common.loading') || '...'
                : t('integrations.wfirma.sendPeriod') || 'Отправить в wFirma'}
            </button>
          </div>
          {periodError && (
            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{periodError}</div>
          )}
          {periodResult && (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-sm space-y-1">
              <p>
                <strong>{t('integrations.wfirma.resultTotal') || 'В выборке'}:</strong> {periodResult.totalSelected}
              </p>
              <p className="text-green-700">
                <strong>{t('integrations.wfirma.resultSuccess') || 'Отправлено'}:</strong> {periodResult.success}
              </p>
              <p className="text-amber-700">
                <strong>{t('integrations.wfirma.resultSkipped') || 'Пропущено (уже были в wFirma)'}:</strong>{' '}
                {periodResult.skippedAlreadySynced}
              </p>
              <p className="text-red-700">
                <strong>{t('integrations.wfirma.resultFailed') || 'Ошибок'}:</strong> {periodResult.failed}
              </p>
              {periodResult.errorMessages?.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-gray-700">{t('integrations.wfirma.errorList') || 'Список ошибок'}</summary>
                  <ul className="mt-2 list-disc pl-5 text-xs text-red-800 max-h-48 overflow-y-auto">
                    {periodResult.errorMessages.map((msg, i) => (
                      <li key={i}>{msg}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      )}

      {hasConfig && (
        <div className="mt-8 pt-8 border-t border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-2">
            {t('integrations.wfirma.resetInvoicesTitle') || 'Сброс отправленных фактур в черновики'}
          </h4>
          <p className="text-sm text-gray-600 mb-4">
            {t('integrations.wfirma.resetInvoicesHint') ||
              'Для фактур со статусом Отправлен за период будет выставлен статус Черновик и очищен wFirma ID. Это удобно перед ручной отправкой по одной фактуре.'}
          </p>
          <div className="flex flex-wrap gap-4 items-end mb-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('integrations.wfirma.periodFrom') || 'С'}</label>
              <input
                type="date"
                value={resetStart}
                onChange={(e) => setResetStart(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('integrations.wfirma.periodTo') || 'По'}</label>
              <input
                type="date"
                value={resetEnd}
                onChange={(e) => setResetEnd(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('integrations.wfirma.dateField') || 'Период по'}</label>
              <select
                value={resetDateField}
                onChange={(e) => setResetDateField(e.target.value as 'ISSUE' | 'SALE')}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="ISSUE">{t('integrations.wfirma.dateFieldIssue') || 'Дата выставления'}</option>
                <option value="SALE">{t('integrations.wfirma.dateFieldSale') || 'Дата продажи'}</option>
              </select>
            </div>
            <div className="w-full">
              <p className="block text-xs text-gray-500 mb-1">
                {t('integrations.wfirma.resetStatusesLabel') || 'Какие статусы сбрасывать'}
              </p>
              <div className="flex flex-wrap gap-3">
                {['SENT', 'PARTIALLY_PAID', 'OVERDUE', 'CANCELLED', 'PAID', 'DRAFT'].map((status) => (
                  <label key={status} className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={resetStatuses.includes(status)}
                      onChange={() => toggleResetStatus(status)}
                    />
                    <span>{t(`companies.invoices.statuses.${status}` as any) || status}</span>
                  </label>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={handleResetInvoicesToDraft}
              disabled={resetLoading}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
            >
              {resetLoading
                ? t('common.loading') || '...'
                : t('integrations.wfirma.resetToDraftButton') || 'Сбросить в черновики'}
            </button>
          </div>
          {resetError && (
            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{resetError}</div>
          )}
          {resetResult && (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-sm space-y-1">
              <p>
                <strong>{t('integrations.wfirma.resultTotal') || 'В выборке'}:</strong> {resetResult.totalSelected}
              </p>
              <p className="text-green-700">
                <strong>{t('integrations.wfirma.resetToDraftResult') || 'Переведено в черновики'}:</strong> {resetResult.resetToDraft}
              </p>
              <p className="text-amber-700">
                <strong>{t('integrations.wfirma.resetSkippedNotSent') || 'Пропущено (статус не выбран)'}:</strong> {resetResult.skippedByStatus}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WFirmaIntegrationSettings;
